import React from "react";
import { CloseIcon } from "../../../assets/icons";
import cx from "classnames";
import { createCustomer } from "../../../backend-services";
import CreateForm from "./CreateForm";
import { CustomerT, newCustomerT } from "../../../types";
import { ICustomer } from "../../../@types";
import { validatePhoneNumber } from "../../../utils";
import { showToast } from "../../../utils/toastUtils";

interface CreateCustomerProps {
  onCustomerCreate?: (customer: any) => void;
  show: boolean;
  onClose?: () => void;
  fetchCustomers?: () => void;
  customerListNavigate?: boolean;
  createCustomers?: (newCustomer: any) => void;
}

const CreateCustomer = ({
  onCustomerCreate,
  show,
  onClose,
  fetchCustomers,
  customerListNavigate,
  createCustomers,
}: CreateCustomerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [backendErrorMessage, setBackendErrorMessage] = React.useState("");
  const [formData, setFormData] = React.useState<ICustomer>({} as ICustomer);

  function isErrorResponse(
    error: any,
  ): error is { response: { data: { message: string } } } {
    return (
      typeof error === "object" &&
      error.response &&
      typeof error.response === "object" &&
      error.response.data &&
      typeof error.response.data === "object" &&
      error.response.data.message
    );
  }

  const handleCreateCustomer = async (customer: CustomerT) => {
    setIsLoading(true);
    setErrorMessage("");
    setBackendErrorMessage("");

    try {
      let data;
      if (onCustomerCreate) {
        data = await createCustomer({
          ...customer,
          phone: customer.phone,
          isOptedIn: false,
          outletId: "",
        });
      } else if (createCustomers) {
        data = createCustomers({
          ...customer,
          phone: customer.phone,
          outletId: "",
        });
      }

      setIsLoading(false);

      // If data is returned, assume the customer was created successfully
      if (data) {
        fetchCustomers && fetchCustomers();
        setIsOpen(false);
        showToast("Customer created successfully!", "success");

        if (onCustomerCreate) {
          onCustomerCreate({
            id: data.id,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            businessName: data.businessName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            state: data.state,
          });
        }
      }
    } catch (error: any) {
      setIsLoading(false);

      if (isErrorResponse(error)) {
        const errorMessage = error.response.data.message;
        setErrorMessage(errorMessage);
        setBackendErrorMessage(errorMessage);

        // Check if the error message indicates a duplicate customer
        if (errorMessage.includes("already exists")) {
          showToast(
            "A customer with this phone number or email already exists.",
            "error",
          );
        } else {
          showToast(errorMessage, "error");
        }
      } else {
        setErrorMessage(error.message);
        setBackendErrorMessage(error.message);
        showToast("An unexpected error occurred. Please try again.", "error");
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose && onClose();
  };

  React.useEffect(() => {
    setIsOpen(show);
  }, [show]);

  return isOpen ? (
    <>
      {/* Modal Background */}
      <div
        aria-hidden="false"
        className="lg:hidden md:hidden fixed top-0 right-0 left-0 z-50 h-full w-full overflow-y-auto overflow-x-hidden items-start justify-end flex bg-gray-900 bg-opacity-50 "
      >
        <div
          className={cx({
            "relative z-50 overflow-y-auto": true,
            "p-5 w-full bg-white shadow max-h-screen overflow-y-auto 540:h-[600vh] lg:h-[200vh] xl:h-[200vh]":
              true,
            // 'animate-slide-in': isOpen,
          })}
        >
          {/* MODAL HEADER */}
          <div className="flex items-center justify-between">
            <h3 className="text-[24px] font-medium">{"Add Customer"}</h3>

            <button
              aria-label="Close"
              className="flex items-center justify-center h-11 w-11 pr-[1em]"
              type="button"
              onClick={handleClose}
            >
              <CloseIcon color="#212121" />
            </button>
          </div>

          {/* MODAL BODY */}
          <div className="mt-10 w-full">
            <CreateForm
              isLoading={isLoading}
              show={true}
              onSubmit={handleCreateCustomer}
              buttontitle={"Add"}
              backendErrorMessage={backendErrorMessage}
              setFormData={setFormData}
              onClose={handleClose}
            />
          </div>
        </div>
      </div>
    </>
  ) : null;
};

export { CreateCustomer };
